import type { DataQualityReport, StabilityLevel } from '@commissionsight/sdk';
import { formatPeriod, parsePeriod, previousMonth } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { Card } from './ui.js';

const STYLES: Record<StabilityLevel, { dot: string; chip: string; label: string }> = {
  ok: {
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    label: 'Normal',
  },
  watch: {
    dot: 'bg-amber-400',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    label: 'Watch',
  },
  alert: {
    dot: 'bg-rose-500',
    chip: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    label: 'Alert',
  },
};

/**
 * Statement-quality monitor — the intelligence layer's data-quality signal.
 * Surfaces carriers with abnormal month-over-month movement (mass fallout +
 * mass new in one period, or very high fallout) — usually a sign the uploaded
 * statement is incomplete or the wrong period, worth a check before acting.
 */
export function DataQualityCard({
  period,
  workspaceId,
}: { period?: string; workspaceId?: string }) {
  const { client } = useAuth();
  const [report, setReport] = useState<DataQualityReport | null>(null);

  useEffect(() => {
    client
      .dataQuality(period || undefined, workspaceId || undefined)
      .then(setReport)
      .catch(() => setReport(null));
  }, [client, period, workspaceId]);

  if (!report || report.carriers.length === 0) return null;
  const flagged = report.carriers.filter((c) => c.level !== 'ok');

  // Member-level drill-down: the Compare page pre-filled with this carrier and
  // the flagged month vs the month before it — the exact diff behind the flag.
  const compareHref = (carrierId: string): string => {
    const qs = new URLSearchParams({ carrierId });
    if (report.period) {
      qs.set('from', formatPeriod(previousMonth(parsePeriod(report.period))));
      qs.set('to', report.period);
    }
    if (workspaceId) qs.set('workspaceId', workspaceId);
    return `/compare?${qs}`;
  };

  return (
    <Card>
      <div className="mb-1 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold">
          <ShieldIcon />
          Statement quality
        </h2>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STYLES[report.overall].chip}`}
        >
          {report.overall === 'ok' ? 'All statements normal' : STYLES[report.overall].label}
        </span>
      </div>
      <p className="mb-4 text-xs text-neutral-500">
        Flags carriers with abnormal month-over-month movement — usually a sign a statement is
        incomplete or the wrong file was uploaded. Checked every period, per carrier. Click a
        flagged carrier to see the member-level comparison behind it.
      </p>

      {flagged.length === 0 ? (
        <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
          ✓ Month-over-month movement looks normal across all {report.carriers.length} carriers this
          period.
        </p>
      ) : (
        <ul className="space-y-2">
          {flagged.map((c) => (
            <li key={c.carrierId}>
              <Link
                to={compareHref(c.carrierId)}
                className="block rounded-lg border border-neutral-200 p-3 transition hover:border-emerald-400 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:border-emerald-600/60 dark:hover:bg-neutral-800/40"
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span className={`h-2 w-2 rounded-full ${STYLES[c.level].dot}`} />
                    {c.carrierName ?? `${c.carrierId.slice(0, 8)}…`}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STYLES[c.level].chip}`}
                  >
                    {STYLES[c.level].label}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-300">{c.reason}</p>
                <p className="mt-1 flex items-center justify-between text-xs text-neutral-400">
                  <span>
                    {c.red.toLocaleString()} dropped · {c.newMembers.toLocaleString()} new ·{' '}
                    {c.present.toLocaleString()} present
                  </span>
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    Review members →
                  </span>
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9 12l2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
